/* istanbul ignore */
import React, { PropsWithChildren, useEffect, useRef, useState } from 'react'
import {
  Overlay as RadixDialogOverlay,
  Content,
  Close,
  Root,
  Portal,
  Title,
  Description,
} from '@radix-ui/react-dialog'
import {
  css,
  keyframes,
  styled,
  useReducedMotion,
} from '@moonpig/launchpad-utils'
import {
  BreakpointsInterface,
  ColorsInterface,
  colorValue,
  spacingPx,
} from '@moonpig/launchpad-theme'
import { system as s } from '@moonpig/launchpad-system'
import {
  IconSystemChevronLeft,
  IconSystemChevronRight,
  IconSystemCross,
} from '@moonpig/launchpad-assets'
import { useLocaleText } from '@moonpig/launchpad-localisation'
import {
  Box,
  IconButton,
  ScreenReaderOnly,
} from '@moonpig/launchpad-components'

type Breakpoints = keyof BreakpointsInterface
type ContentWidth = Partial<Record<Breakpoints, string>>
const SCROLLBAR_WIDTH = 20
export type Labels = {
  backButton?: string
  closeButton?: string
}
export type Side = 'left' | 'right'
export type SideModalProps = {
  isOpen: boolean
  onDismiss: (isOpen: boolean) => void
  preventESCDismiss?: boolean
  heading?: React.ReactNode | string
  description: string
  backgroundColor?: keyof ColorsInterface
  backButton?: boolean
  onBack?: () => void
  labels?: Labels
  width?: ContentWidth
  maxWidth?: ContentWidth | null
  minWidth?: ContentWidth | null
  triggerRef?: React.RefObject<HTMLElement>
  side?: Side
  // bellow are props added to the cloned SideModal
  // custom header has own close icons and can have sticky style
  customHeader?: React.ReactNode | string
  isRadialBackground?: boolean
  isChatVariant?: boolean
}

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`

const slideInRight = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0.5;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`
const slideInLeft = keyframes`
    from {
      transform: translateX(-100%);
      opacity: 0.5;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  `
const slideOutRight = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0.5;
  }
`
const slideOutLeft = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(-100%);
    opacity: 0.5;
  }
`
const animationBezier = 'cubic-bezier(0.56, 0.1, 0.34, 0.91)'
const slideInAnimation = (side: 'left' | 'right') => css`
  animation: 450ms ${animationBezier}
    ${side === 'left' ? slideInLeft : slideInRight} forwards;
`
// istanbul ignore next
const slideOutAnimation = (side: 'left' | 'right') => css`
  animation: 450ms ${animationBezier} /* istanbul ignore next */
    ${side === 'left' ? slideOutLeft : slideOutRight} forwards;
`

const fadeInAnimation = css`
  animation: 450ms ${animationBezier} ${fadeIn} forwards;
`

const fadeOutAnimation = css`
  animation: 450ms ${animationBezier} ${fadeOut} forwards;
`

const StyledDialogOverlay = styled.div<{ isClosing: boolean }>`
  position: fixed;
  inset: 0;
  overflow-y: auto;
  ${s({
    zIndex: 'zIndexModal',
  })}
  &::before {
    background: rgba(0, 0, 0, 0.6);
    content: ' ';
    position: fixed;
    inset: 0;
  }
  ${({ isClosing }) =>
    isClosing !== null && (isClosing ? fadeOutAnimation : fadeInAnimation)}
  @media (prefers-reduced-motion) {
    animation: none;
  }
`

const radialBackground = css`
  background:
    radial-gradient(
      88% 26% at 50% 100%,
      rgba(255, 211, 219, 0.82) 0%,
      rgba(255, 211, 219, 0) 100%
    ),
    radial-gradient(
      48% 22% at 0% 100%,
      rgba(255, 233, 238, 0.72) 0%,
      rgba(255, 233, 238, 0) 100%
    ),
    radial-gradient(
      48% 22% at 100% 100%,
      rgba(230, 238, 250, 0.84) 0%,
      rgba(230, 238, 250, 0) 100%
    ),
    radial-gradient(
      87.14% 14.69% at 50% 95.44%,
      ${colorValue('colorBackground03')} 0%,
      ${colorValue('colorBackground01')}
    ),
    ${colorValue('colorBackground01')};
`

const withLeftBorder = css`
    ${s({
      borderLeft: 1,
      borderColor: 'colorBorder03',
      boxShadow: 'boxShadow02',
    })}
`

//overrides Box padding
const noPadding = css`
  padding-left: 0;
  padding-right: 0;
  padding-top: 0;
  padding-bottom: 0;
`

// othervise vertical scroll will move component but not content up
const fullHeight = css`
  overflow: hidden;
  max-height: 100dvh;
  height: 100%;
`

const StyledDialogContent = styled(Box)<
  Pick<
    SideModalProps,
    | 'backgroundColor'
    | 'width'
    | 'side'
    | 'isRadialBackground'
    | 'isChatVariant'
  > & {
    isClosing: boolean
  }
>`
  position: absolute;
  right: 0;
  display: flex;
  flex-direction: column;
  ${({ side }) => (side === 'left' ? { left: 0 } : { right: 0 })};
  ${({ backgroundColor }) =>
    s({
      bgcolor: backgroundColor || 'colorBackgroundSite',
      p: 6,
      zIndex: 'zIndexModal',
    })}
  min-height: 100%;
  ${({ isClosing, side }) =>
    isClosing !== null &&
    side &&
    (isClosing ? slideOutAnimation(side) : slideInAnimation(side))};
  @media (prefers-reduced-motion) {
    animation: none;
  }
  ${({ isRadialBackground }) => isRadialBackground && radialBackground}        
  ${({ isChatVariant }) => isChatVariant && [withLeftBorder, noPadding, fullHeight]}
`

const StyledDialogCloseWrapper = styled.div<Pick<SideModalProps, 'side'>>`
  display: flex;
  flex-direction: ${({ side }) => (side === 'left' ? 'row-reverse' : 'row')};
  justify-content: space-between;
  align-items: center;
  min-height: 48px;
  position: relative;
  gap: ${spacingPx(6)};
  ${s({
    mb: 4,
    zIndex: 'zIndexElements',
  })};
`
const StyledBackButton = styled(IconButton)<Pick<SideModalProps, 'side'>>`
  ${({ side }) =>
    s({
      mr: side === 'left' ? -4 : 'auto',
      ml: side === 'left' ? 'auto' : -4,
    })}
`
const StyledDialogClose = styled(Close)<Pick<SideModalProps, 'side'>>`
  ${({ side }) =>
    s({
      mr: side === 'left' ? 'auto' : -4,
      ml: side === 'left' ? -4 : 'auto',
    })}
`
const StyledHeading = styled.h2`
  ${s({
    typography: { xs: 'typeMobileDisplay03', md: 'typeDesktopDisplay05' },
    m: 0,
  })}
`
export const SideModal: React.FC<PropsWithChildren<SideModalProps>> = ({
  isOpen,
  onDismiss,
  children,
  preventESCDismiss = false,
  heading,
  description,
  backgroundColor,
  backButton,
  onBack,
  labels,
  width = { xs: '100%', md: '33%' },
  maxWidth = { md: '450px' },
  minWidth = { md: '375px' },
  triggerRef,
  side = 'right',
  customHeader,
  isRadialBackground,
  isChatVariant,
}) => {
  const localiseText = useLocaleText()
  const [open, setOpen] = useState(isOpen)
  const [isClosing, setIsClosing] = useState(false)
  const reduceMotion = useReducedMotion()

  useEffect(() => {
    setOpen(isOpen)
    setIsClosing(false)
  }, [isOpen])

  const firstUpdate = useRef(true)
  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false
      return
    }
    if (!isOpen && triggerRef?.current) {
      triggerRef.current.focus()
    }
  }, [isOpen, triggerRef])
  const renderHeading = () => {
    if (heading) {
      if (typeof heading === 'string') {
        return <StyledHeading>{heading}</StyledHeading>
      }
      return heading
    }
    return (
      <ScreenReaderOnly>{localiseText('modal.description')}</ScreenReaderOnly>
    )
  }
  const renderScreenReaderHeading = () => {
    if (typeof heading === 'string' && heading.trim()) {
      return <ScreenReaderOnly>{heading}</ScreenReaderOnly>
    }
    return (
      <ScreenReaderOnly>{localiseText('modal.description')}</ScreenReaderOnly>
    )
  }

  return (
    <Root
      open={open}
      onOpenChange={nextOpen => {
        // istanbul ignore next
        if (nextOpen) {
          return
        }
        if (reduceMotion) {
          setOpen(false)
          setIsClosing(false)
          onDismiss(false)
          return
        }
        setIsClosing(true)
      }}
    >
      <Portal>
        <RadixDialogOverlay asChild className="DialogOverlay">
          <StyledDialogOverlay
            isClosing={isClosing}
            data-testid="lpd-side-modal-overlay"
          >
            <Content
              asChild
              onEscapeKeyDown={event => {
                if (preventESCDismiss) {
                  event.preventDefault()
                }
              }}
              onPointerDownOutside={
                /* istanbul ignore next */ e => {
                  const currentTarget = e.currentTarget as HTMLElement
                  // Hack to prevent clicking the scrollbar from closing the modal
                  // Needs to be revisited
                  if (
                    e.detail.originalEvent.offsetX >
                    currentTarget.clientWidth - SCROLLBAR_WIDTH
                  ) {
                    e.preventDefault()
                  }
                }
              }
            >
              <StyledDialogContent
                data-testid="lpd-side-modal-content"
                width={width}
                minWidth={minWidth}
                maxWidth={maxWidth}
                isClosing={isClosing}
                backgroundColor={backgroundColor}
                onAnimationEnd={() => {
                  if (isClosing) {
                    setOpen(false)
                    setIsClosing(false)
                    onDismiss(false)
                  }
                }}
                side={side}
                isRadialBackground={isRadialBackground}
                isChatVariant={isChatVariant}
              >
                {customHeader ? (
                  <>
                    <Title asChild>{renderScreenReaderHeading()}</Title>
                    {customHeader}
                  </>
                ) : (
                  <StyledDialogCloseWrapper side={side}>
                    {backButton && onBack && (
                      <StyledBackButton
                        icon={
                          side === 'left'
                            ? IconSystemChevronRight
                            : IconSystemChevronLeft
                        }
                        label={
                          labels?.backButton ||
                          localiseText('common.side-modal.back.label')
                        }
                        onClick={onBack}
                        side={side}
                      />
                    )}
                    <Title asChild>{renderHeading()}</Title>
                    <StyledDialogClose asChild side={side}>
                      <IconButton
                        icon={IconSystemCross}
                        label={
                          labels?.closeButton ||
                          localiseText('common.side-modal.close.label')
                        }
                      />
                    </StyledDialogClose>
                  </StyledDialogCloseWrapper>
                )}
                <ScreenReaderOnly>
                  <Description>
                    {description || localiseText('modal.description')}
                  </Description>
                </ScreenReaderOnly>
                {children}
              </StyledDialogContent>
            </Content>
          </StyledDialogOverlay>
        </RadixDialogOverlay>
      </Portal>
    </Root>
  )
}
